import type { PopupContentSettings, PopupQuestion } from "@/shared/types/popup-types";
import { wheelPlayable, wheelFormPlacement } from "./wheel";

/**
 * The GATE step — where the lead is actually collected. A playable wheel
 * with the form INLINE replaces the welcome form outright (fields sit beside
 * the wheel and the submit IS the spin); every other configuration gates on
 * the classic welcome form.
 */
export function gateStep(
  content: PopupContentSettings | null | undefined
): "wheel" | "welcome" {
  return wheelPlayable(content) && wheelFormPlacement(content) === "inline"
    ? "wheel"
    : "welcome";
}

/**
 * Where the welcome form's submit lands when the wheel plays AFTER it
 * (form_placement "before"): the wheel page, with the lead held un-persisted
 * until the spin — exactly the deck's contract. Null = the form submits.
 */
export function stepAfterWelcome(
  content: PopupContentSettings | null | undefined
): "wheel" | null {
  return wheelPlayable(content) && wheelFormPlacement(content) === "before"
    ? "wheel"
    : null;
}

/**
 * The step in front of the gate. A play-FIRST game (form_placement "after")
 * shows before the lead is asked for: the server names and signs the prize,
 * the visitor sees it, and only then does the form appear. Otherwise the
 * gate itself.
 */
export function preGateStep(
  content: PopupContentSettings | null | undefined
): "wheel" | "welcome" {
  return wheelPlayable(content) && wheelFormPlacement(content) === "after"
    ? "wheel"
    : gateStep(content);
}

/** Where the game page lands once the prize has been shown: the welcome
 *  form when the game played first, else the success step. */
export function stepAfterWheel(
  content: PopupContentSettings | null | undefined
): "welcome" | "success" {
  return wheelPlayable(content) && wheelFormPlacement(content) === "after"
    ? "welcome"
    : "success";
}

/**
 * A scratch page only plays when it's enabled AND has a prize to reveal —
 * scratching a cover off an empty card is a dead end, so a prize-less config
 * behaves as if the step didn't exist. Mirrored by ScratchStage's own guard.
 */
export function scratchPlayable(
  content: PopupContentSettings | null | undefined
): boolean {
  const s = content?.scratch;
  return !!s?.enabled && (s.prize_text || "").trim().length > 0;
}

const KEY_RE = /^[a-zA-Z0-9_-]{1,64}$/;

/** The question pages that can actually play: enabled, keyed, ≥2 labelled
 *  answers. A broken page is skipped rather than shown as a dead end. */
export function playableQuestions(
  content: PopupContentSettings | null | undefined
): PopupQuestion[] {
  const q = content?.questions;
  if (!q?.enabled || !Array.isArray(q.items)) return [];
  return q.items.filter(
    (it) =>
      it &&
      KEY_RE.test(String(it.key || "")) &&
      Array.isArray(it.options) &&
      it.options.filter((o) => o && String(o.label || "").trim()).length >= 1
  );
}

export function questionsPlayable(
  content: PopupContentSettings | null | undefined
): boolean {
  return playableQuestions(content).length > 0;
}

/** After the last question (or when there are none): the gate. */
export function stepAfterQuestion(
  content: PopupContentSettings | null | undefined,
  index: number
): "question" | "wheel" | "welcome" {
  return index + 1 < playableQuestions(content).length ? "question" : preGateStep(content);
}

/**
 * Which step a popup's flow OPENS on. "intro" only when a playable intro
 * page is configured: enabled AND carrying some copy — an enabled-but-empty
 * intro must never gate the form behind a blank page with an unlabeled
 * button. With no intro, a playable scratch page opens the flow instead
 * (the hook comes before the ask). One definition, used by every open path
 * (trigger, page embed, previews), so the flow can't start on different
 * steps in different modes.
 */
export function firstPopupStep(
  content: PopupContentSettings | null | undefined
): "intro" | "scratch" | "question" | "wheel" | "welcome" {
  const intro = content?.intro;
  if (intro?.enabled) {
    const hasCopy =
      (intro.heading || "").trim().length > 0 ||
      (intro.sub_heading || "").trim().length > 0;
    if (hasCopy) return "intro";
  }
  if (scratchPlayable(content)) return "scratch";
  return questionsPlayable(content) ? "question" : preGateStep(content);
}

/**
 * Where the intro's CTA lands: the scratch page when one is playable, else
 * the first question page, else the gate.
 */
export function stepAfterIntro(
  content: PopupContentSettings | null | undefined
): "scratch" | "question" | "wheel" | "welcome" {
  if (scratchPlayable(content)) return "scratch";
  return questionsPlayable(content) ? "question" : preGateStep(content);
}

/**
 * The intro CTA's link, when the intro is a pure announcement: a trimmed
 * http(s) URL from `intro.button_url`, else null (the CTA advances the flow).
 * Only http/https pass — a `javascript:` or protocol-less value can never
 * become a navigation.
 */
export function introCtaUrl(
  content: PopupContentSettings | null | undefined
): string | null {
  return safeCtaUrl(content?.intro?.button_url);
}

/**
 * The thank-you button's link (`success.button_url`), same rules as the
 * intro's: http/https only, else null (the button just closes the popup).
 */
export function successCtaUrl(
  content: PopupContentSettings | null | undefined
): string | null {
  return safeCtaUrl(content?.success?.button_url);
}

/** A trimmed, parseable http(s) URL, or null. */
export function safeCtaUrl(value: string | null | undefined): string | null {
  const raw = (value || "").trim();
  if (!/^https?:\/\//i.test(raw)) return null;
  try {
    return new URL(raw).href;
  } catch {
    return null;
  }
}

/** Where the scratch page's Claim CTA lands: the questions, else the gate. */
export function stepAfterScratch(
  content: PopupContentSettings | null | undefined
): "question" | "wheel" | "welcome" {
  return questionsPlayable(content) ? "question" : preGateStep(content);
}

/** The phone page plays when enabled with a heading. It sits right after the
 *  welcome form on the classic path (no deck, no wheel-after-form): the form
 *  holds the lead, the phone page (or its skip link) submits. */
export function phonePagePlayable(
  content: PopupContentSettings | null | undefined
): boolean {
  const p = content?.phone_page;
  return !!p?.enabled && String(p.heading || "").trim().length > 0;
}
